/**
 * Enrich outbound scheduler links with tracking UTM params on click.
 *
 * Links with a `data-enrich-utm` attribute will have:
 * - `utm_source` set to `gotopo.{event}` where `{event}` is read from `data-dl-event`
 *   (e.g., `data-dl-event="generate_lead"` → `utm_source=gotopo.generate_lead`)
 * - `utm_content` set to GA4 client_id — prefers the sGTM server-managed
 *   `FPIDP` cookie (which matches `user_pseudo_id` in BigQuery), falling
 *   back to the browser `_ga` cookie when `FPIDP` is unavailable
 * - `utm_term` set to GA4 session_id (when cookie available)
 *
 * The `data-enrich-utm` attribute can optionally provide an explicit value to
 * override `data-dl-event` if the webhook intent needs to differ from the
 * dataLayer event name. In practice, they're the same.
 *
 * The webhook controller uses the `gotopo.*` utm_source prefix to identify
 * trackable bookings, and `utm_content`/`utm_term` for GA4 session attribution.
 *
 * Graceful degradation: if GA4 cookies are absent (ad blocker, no consent),
 * `utm_source` is still set so the webhook logs the booking. GA4 attribution
 * is skipped since there's no session to attribute to.
 *
 * FPIDP context: when sGTM uses "Server Managed" client IDs, it replaces
 * the browser `_ga` client_id with a server-managed `FPID` hash (HttpOnly).
 * The Cookie Monster tag in sGTM exposes this value as the JS-readable
 * `FPIDP` cookie. We prefer `FPIDP` so the webhook sends the same client_id
 * that sGTM uses for client-side events, ensuring `user_pseudo_id` matches
 * in BigQuery.
 *
 * @example
 * ```html
 * <a href="https://scheduler.zoom.us/pierce/intro"
 *    data-dl-event="generate_lead" data-enrich-utm>
 *   Schedule a Call
 * </a>
 * ```
 */

/**
 * Read the server-managed client_id from the `FPIDP` cookie.
 *
 * The `FPIDP` cookie is a JS-accessible duplicate of the `FPID` cookie set
 * by the Cookie Monster tag in sGTM. Its value matches the `user_pseudo_id`
 * stored in BigQuery when the GA4 client uses "Server Managed" client IDs.
 *
 * Cookie format: `FPID2.2.<base64_hash>.<timestamp>`
 * The client_id is the last two dot-separated segments: `<base64_hash>.<timestamp>`
 */
export function getFPIDClientId(): string | null {
  const match = /(?:^|;\s*)FPIDP=([^;]+)/.exec(document.cookie);
  if (match === null) return null;

  const raw = match[1];
  if (raw === undefined) return null;

  // The FPID cookie value may contain URL-encoded characters (e.g., base64
  // padding `=` stored as `%3D`). Decode so the client_id matches the raw
  // `user_pseudo_id` value in BigQuery.
  const value = decodeURIComponent(raw);

  const parts = value.split('.');
  if (parts.length < 4) return null;

  // Client ID is the last two segments: "<base64_hash>.<timestamp>"
  const a = parts[parts.length - 2];
  const b = parts[parts.length - 1];
  if (a === undefined || b === undefined) return null;

  return `${a}.${b}`;
}

/**
 * Read the GA4 client_id from the `_ga` cookie.
 *
 * Used as a fallback when the `FPIDP` cookie is not available (e.g., first
 * page load before sGTM responds, or sGTM not using server-managed IDs).
 *
 * Cookie format: `GA1.1.XXXXXXXXXX.XXXXXXXXXX`
 * The client_id is the last two dot-separated segments.
 */
export function getGA4ClientId(): string | null {
  const match = /(?:^|;\s*)_ga=([^;]+)/.exec(document.cookie);
  if (match === null) return null;

  const value = match[1];
  if (value === undefined) return null;

  const parts = value.split('.');
  if (parts.length < 4) return null;

  // Client ID is the last two segments: "XXXXXXXXXX.XXXXXXXXXX"
  const a = parts[parts.length - 2];
  const b = parts[parts.length - 1];
  if (a === undefined || b === undefined) return null;

  return `${a}.${b}`;
}

/**
 * Read the GA4 session_id from the `_ga_<SUFFIX>` cookie.
 *
 * The suffix is the GA4 measurement ID without the `G-` prefix, read from
 * a `<meta name="ga4-measurement-id">` tag in the document head.
 *
 * Cookie formats (third dot-separated segment):
 *   Legacy: `GS1.1.1674465678.1.0.1674465678.0.0.0` — session_id is a plain number
 *   Current: `GS2.1.s1770418105$o4$g1$t1770418434$j8$l0$h1308631280` — session_id follows the `s` prefix
 */
export function getGA4SessionId(): string | null {
  const suffix = getGA4MeasurementIdSuffix();
  if (suffix === null) return null;

  const cookieName = `_ga_${suffix}`;
  const escaped = cookieName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = new RegExp(`(?:^|;\\s*)${escaped}=([^;]+)`).exec(document.cookie);
  if (match === null) return null;

  const value = match[1];
  if (value === undefined) return null;

  const parts = value.split('.');
  // Session ID is in the third segment (index 2)
  const segment = parts[2];
  if (segment === undefined) return null;

  // Current format: "s1770418105$o4$g1$..." — extract number after "s"
  const compoundMatch = /^s(\d+)/.exec(segment);
  if (compoundMatch?.[1] !== undefined) return compoundMatch[1];

  // Legacy format: plain numeric session_id
  if (/^\d+$/.test(segment)) return segment;

  return null;
}

/**
 * Read the GA4 measurement ID suffix from a meta tag.
 * Falls back to null if the meta tag is not present.
 */
function getGA4MeasurementIdSuffix(): string | null {
  const meta = document.querySelector('meta[name="ga4-measurement-id"]');
  if (meta === null) return null;

  const measurementId = meta.getAttribute('content');
  if (measurementId === null || measurementId === '') return null;

  // Strip the "G-" prefix to get the suffix used in cookie names
  return measurementId.replace(/^G-/, '');
}

/** Prefix for utm_source values that the webhook controller recognizes. */
const UTM_SOURCE_PREFIX = 'gotopo';

/**
 * Initialize UTM enrichment on all links with the `data-enrich-utm` attribute.
 * The event intent is read from `data-dl-event` (the single source of truth).
 * `data-enrich-utm` can optionally override the event name if they need to diverge.
 */
export function initSchedulerLinks(): void {
  const links = document.querySelectorAll<HTMLAnchorElement>('a[data-enrich-utm]');

  links.forEach((link) => {
    link.addEventListener('click', () => {
      const enrichUtm = link.getAttribute('data-enrich-utm');
      const dlEvent = link.getAttribute('data-dl-event');
      // Prefer explicit data-enrich-utm value; fall back to data-dl-event
      const eventName = enrichUtm !== null && enrichUtm !== '' ? enrichUtm : dlEvent;
      if (eventName === null || eventName === '') return;

      try {
        const url = new URL(link.href);

        // Always set utm_source so the webhook knows the intent from our site
        url.searchParams.set('utm_source', `${UTM_SOURCE_PREFIX}.${eventName}`);

        // Prefer FPIDP (server-managed) over _ga (browser-managed) so the
        // webhook client_id matches sGTM's user_pseudo_id in BigQuery.
        const clientId = getFPIDClientId() ?? getGA4ClientId();
        if (clientId !== null) {
          url.searchParams.set('utm_content', clientId);

          const sessionId = getGA4SessionId();
          if (sessionId !== null) {
            url.searchParams.set('utm_term', sessionId);
          }
        }

        link.href = url.toString();
      } catch {
        // If URL parsing fails, let the link work as-is
      }
    });
  });
}
